import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { CircuitBenefitType } from '@/types/circuits/CircuitBenefitType';
import { parseCircuitBenefitType } from '@/utils/circuits/parseCircuitBenefitType';
import { useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';

interface DisplaySelectionTableProps {
  control: Control<CircuitFormDataWithTranslations>;
  index: number;
  stepIndex: number;
  type: CircuitBenefitType;
  isViewMode?: boolean;
}

const DisplaySelectionTable = ({
  control,
  index,
  stepIndex,
  type,
  isViewMode,
}: DisplaySelectionTableProps) => {
  const { t } = useTranslation();

  return (
    <table className="w-full">
      <thead>
        <tr>
          <th className="text-left">
            <Label className="font-bold text-gray-700">
              {t('circuits.editorial.tables.display')}
            </Label>
          </th>
          <th className="text-center w-16">
            <Label>{t('circuits.editorial.tables.standard')}</Label>
          </th>
          <th className="text-center w-16">
            <Label>{t('circuits.editorial.tables.option')}</Label>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{t('circuits.editorial.tables.displayName')}</td>
          <td>
            <div className="flex justify-center">
              <BenefitDisplayCheckbox
                control={control}
                cellType="standard"
                index={index}
                stepIndex={stepIndex}
                type={type}
                value="name"
                isViewMode={isViewMode}
              />
            </div>
          </td>
          <td>
            <div className="flex justify-center">
              <BenefitDisplayCheckbox
                control={control}
                cellType="option"
                index={index}
                stepIndex={stepIndex}
                type={type}
                value="name"
                isViewMode={isViewMode}
              />
            </div>
          </td>
        </tr>
        <tr>
          <td>{t('circuits.editorial.tables.displayShortDescription')}</td>
          <td>
            <div className="flex justify-center">
              <BenefitDisplayCheckbox
                control={control}
                cellType="standard"
                index={index}
                stepIndex={stepIndex}
                type={type}
                value="shortDescription"
                isViewMode={isViewMode}
              />
            </div>
          </td>
          <td>
            <div className="flex justify-center">
              <BenefitDisplayCheckbox
                control={control}
                cellType="option"
                index={index}
                stepIndex={stepIndex}
                type={type}
                value="shortDescription"
                isViewMode={isViewMode}
              />
            </div>
          </td>
        </tr>
        <tr>
          <td>{t('circuits.editorial.tables.displayLongDescription')}</td>
          <td>
            <div className="flex justify-center">
              <BenefitDisplayCheckbox
                control={control}
                cellType="standard"
                index={index}
                stepIndex={stepIndex}
                type={type}
                value="longDescription"
                isViewMode={isViewMode}
              />
            </div>
          </td>
          <td>
            <div className="flex justify-center">
              <BenefitDisplayCheckbox
                control={control}
                cellType="option"
                index={index}
                stepIndex={stepIndex}
                type={type}
                value="longDescription"
                isViewMode={isViewMode}
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default DisplaySelectionTable;

interface BenefitDisplayCheckbox {
  control: Control<CircuitFormDataWithTranslations>;
  index: number;
  stepIndex: number;
  type: CircuitBenefitType;
  cellType: 'standard' | 'option';
  value: 'name' | 'shortDescription' | 'longDescription';
  isViewMode?: boolean;
}

const BenefitDisplayCheckbox = ({
  cellType,
  control,
  index,
  stepIndex,
  type,
  value,
  isViewMode,
}: BenefitDisplayCheckbox) => {
  const cellName = useMemo(
    () => (cellType === 'standard' ? 'standardDisplays' : 'optionalDisplays'),
    [cellType],
  );

  const benefitType = useMemo(() => parseCircuitBenefitType(type), [type]);

  return (
    <Controller
      control={control}
      name={`circuitDays.${index}.steps.${stepIndex}.${benefitType}.${cellName}`}
      render={({ field }) => {
        field.value = field.value ?? [];
        return (
          <Checkbox
            {...field}
            checked={field.value.includes(value)}
            onClick={() =>
              field.onChange(
                field.value.includes(value)
                  ? field.value.filter((fieldValue) => fieldValue !== value)
                  : [...field.value, value],
              )
            }
            value={[]}
            disabled={isViewMode}
          />
        );
      }}
    />
  );
};
